import { useCallback, useEffect, useState } from 'react';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  currentSubscription,
  disablePush,
  enablePush,
  isPushSupported,
  permissionState,
  toPayload,
} from '../lib/push';
import { Icon } from './Icon';

/**
 * Notification center — opt in to browser notifications for access requests.
 *
 * Two scopes, and the UI has to be honest about the split or it reads as a bug:
 *   • the MASTER toggle is per BROWSER — the permission grant and the push subscription
 *     both belong to this browser install, not to you.
 *   • the TOPIC checkboxes are per USER — they follow you to your other browsers, so
 *     enabling on a second machine does not read as "my settings reset".
 *
 * Everything is off until you turn it on: the topics default false server-side, and every
 * delivery query inner-joins the prefs row.
 */
const SETTINGS = gql`
  query NotificationSettings($endpoint: String) {
    pushPublicKey
    notificationSettings(endpoint: $endpoint) {
      accessCreated
      accessDecision
      accessMention
      browserCount
      thisBrowserRegistered
    }
  }
`;

const REGISTER = gql`
  mutation RegisterPushSubscription($input: PushSubscriptionInput!) {
    registerPushSubscription(input: $input) {
      accessCreated
      accessDecision
      accessMention
      browserCount
      thisBrowserRegistered
    }
  }
`;

const UNREGISTER = gql`
  mutation UnregisterPushSubscription($endpoint: String!) {
    unregisterPushSubscription(endpoint: $endpoint)
  }
`;

const SET_PREFS = gql`
  mutation SetNotificationPrefs($prefs: NotificationPrefsInput!, $endpoint: String) {
    setNotificationPrefs(prefs: $prefs, endpoint: $endpoint) {
      accessCreated
      accessDecision
      accessMention
      browserCount
      thisBrowserRegistered
    }
  }
`;

interface Settings {
  accessCreated: boolean;
  accessDecision: boolean;
  accessMention: boolean;
  browserCount: number;
  thisBrowserRegistered: boolean;
}

const TOPICS: { key: keyof Settings & string; label: string; hint: string }[] = [
  {
    key: 'accessCreated',
    label: 'A new access request is filed',
    hint: 'Anyone on the team files one. Excludes your own.',
  },
  {
    key: 'accessDecision',
    label: 'A request I filed is decided',
    hint: 'Approved, denied or cancelled.',
  },
  { key: 'accessMention', label: 'Someone @mentions me', hint: 'In a comment on any request.' },
];

export function NotificationCenter() {
  const [endpoint, setEndpoint] = useState<string | null>(null);
  const [permission, setPermission] = useState(permissionState());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Read this browser's existing endpoint first so the server can tell us whether THIS
  // browser is registered (as opposed to some other machine of ours).
  useEffect(() => {
    let live = true;
    void currentSubscription().then((sub) => {
      if (live) setEndpoint(sub?.endpoint ?? null);
    });
    return () => {
      live = false;
    };
  }, []);

  const { data, loading, refetch } = useQuery<{
    pushPublicKey: string | null;
    notificationSettings: Settings;
  }>(SETTINGS, { variables: { endpoint }, fetchPolicy: 'cache-and-network' });

  const [register] = useMutation(REGISTER, { onCompleted: () => refetch() });
  const [unregister] = useMutation(UNREGISTER, { onCompleted: () => refetch() });
  const [setPrefs] = useMutation(SET_PREFS, { onCompleted: () => refetch() });

  const vapidKey = data?.pushPublicKey ?? null;
  const settings = data?.notificationSettings;
  const on = Boolean(settings?.thisBrowserRegistered);

  const enable = useCallback(async () => {
    if (!vapidKey) return;
    setBusy(true);
    setError(null);
    try {
      const payload = await enablePush(vapidKey);
      setPermission(permissionState());
      if (!payload) {
        // Declined, or the browser refused. permissionState() now tells the user which.
        return;
      }
      setEndpoint(payload.endpoint);
      await register({ variables: { input: payload } });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }, [vapidKey, register]);

  const disable = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const dropped = (await disablePush()) ?? endpoint;
      if (dropped) await unregister({ variables: { endpoint: dropped } });
      setEndpoint(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }, [endpoint, unregister]);

  // Push unconfigured for this environment → render NOTHING, heading included.
  // The <section> lives here rather than in AccountPage precisely so this can
  // remove the whole band; a heading in the page would survive and leave an
  // empty "Notification center" with nothing under it.
  //
  // While the first fetch is in flight vapidKey is also undefined, so wait for
  // it rather than rendering the panel and yanking it away a moment later.
  if (!data) return null;
  if (!vapidKey) return null;

  if (!isPushSupported()) {
    return (
      <section>
        <h3 className="section">Notification center</h3>
        <p className="muted text-sm">
          This browser does not support push notifications. OptInfra is a desktop console — try
          Chrome, Edge or Firefox.
        </p>
      </section>
    );
  }

  return (
    <section>
      <h3 className="section">Notification center</h3>
      <p className="detail-desc">
        Get a desktop notification when something happens on an access request — even when
        OptInfra is not the tab you are looking at. Off until you turn it on.
      </p>

      {permission === 'denied' ? (
        <div className="dep-card">
          <p className="text-sm" style={{ margin: 0 }}>
            <Icon name="alert" /> Notifications are <strong>blocked</strong> for this site.
          </p>
          <p className="muted text-xs" style={{ marginBottom: 0 }}>
            A block is permanent until you clear it — the site cannot ask again. Click the icon at
            the left of the address bar → Notifications → Allow, then reload this page.
          </p>
        </div>
      ) : (
        <div className="dep-card" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            type="button"
            className={on ? 'btn btn-sm' : 'btn btn-primary'}
            disabled={busy || !vapidKey}
            /* The permission prompt only appears from inside a user gesture — so the
               request has to start here, in the click handler, not in an effect. */
            onClick={() => void (on ? disable() : enable())}
          >
            {busy ? 'Working…' : on ? 'Turn off on this browser' : 'Enable on this browser'}
          </button>
          <span className="muted text-xs">
            {on ? 'This browser is registered.' : 'You will be asked to allow notifications.'}
            {settings && settings.browserCount > (on ? 1 : 0) ? (
              <> Also enabled on {settings.browserCount - (on ? 1 : 0)} other browser(s).</>
            ) : null}
          </span>
        </div>
      )}

      {error && <p className="error text-sm">{error}</p>}

      <fieldset
        style={{ border: 0, padding: 0, margin: '12px 0 0', opacity: on ? 1 : 0.55 }}
        disabled={!on || busy}
      >
        <legend className="muted text-xs" style={{ padding: 0 }}>
          Notify me when — applies to all your browsers
        </legend>
        {TOPICS.map((t) => (
          <label
            key={t.key}
            className="text-sm"
            style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginTop: 8 }}
          >
            <input
              type="checkbox"
              checked={Boolean(settings?.[t.key])}
              onChange={(e) =>
                void setPrefs({ variables: { prefs: { [t.key]: e.target.checked }, endpoint } })
              }
            />
            <span>
              {t.label} <span className="muted text-xs">— {t.hint}</span>
            </span>
          </label>
        ))}
      </fieldset>
    </section>
  );
}
